import { sanitizeUrl, type UrlClass } from "../private/sanitizeUrl";
import type { ImageSummary, RequestTiming } from "../types";
import {
  ALLOWED_INITIATOR_TYPES,
  STATIC_INITIATOR_TYPES,
} from "./firstApiTiming";

// img entries stay in the accumulator (collectImageSummary folds them into a
// fixed-size aggregate) but get no individual bars — 20~100+ image rows were
// the bridge-payload spike.
// ponytail: CSS background images (initiator "css") deliberately keep their
// bars. Telling them from fonts needs a URL-extension guess that misses
// extensionless CDN images, and the payload is already bounded by the 100-entry
// cap and the 8KB backstop. Fold them in only if the image pressure lane turns
// out to under-read on real data.
const RESOURCE_BAR_INITIATOR_TYPES: ReadonlySet<string> = new Set(
  [...STATIC_INITIATOR_TYPES].filter((type) => type !== "img"),
);

// ponytail: cap at 100 per field — pre-LCP API calls are typically <10 and
// pre-LCP static resources rarely exceed a few dozen; this only guards a
// pathological page from bloating the analytics payload. Bump if a real gantt
// needs more than the earliest 100 bars of a kind.
const MAX_TIMINGS_PER_FIELD = 100;

export interface CollectTimingsOptions {
  maxEntries?: number;
}

// Shape the accumulated entries (gathered by observeResourceTimings, immune to
// resource-buffer eviction) into wire intervals, cut off at `cutoffTime` (the
// LCP render time, or the deadline fallback). Host/deny filtering already
// happened at gather time; the initiator split happens here so API and static
// bars land in their own wire fields with independent caps. Pure transform:
// initiator split → cutoff → drop invalid → sort → cap → map
// (URLs sanitized at this wire boundary — id segments masked, query dropped
// except the asset class's CDN transform params).
//
// Called from the lazy flush, which fires late (interaction / page hide) — by
// then pre-cutoff requests have normally completed and been recorded. A request
// still in-flight at that point has no responseEnd, so it can't be a gantt bar
// and is dropped by the responseEnd guards below.
function collectTimings(
  cutoffTime: number,
  allowedInitiators: ReadonlySet<string>,
  maxEntries: number,
  urlClass: UrlClass,
): RequestTiming[] {
  const entries =
    typeof window !== "undefined"
      ? window.__xviewPerfTiming__?.resourceTimingEntries
      : undefined;
  if (entries === undefined) return [];

  return (
    entries
      .filter((entry) => allowedInitiators.has(entry.initiatorType))
      .filter((entry) => entry.startTime <= cutoffTime)
      // Drop failed/incomplete entries. `> 0` is not implied by the
      // `>= startTime` check: an entry with both at 0 would survive it as a
      // zero-width bar pinned to the time origin.
      .filter((entry) => entry.responseEnd > 0)
      .filter((entry) => entry.responseEnd >= entry.startTime)
      .sort((a, b) => a.startTime - b.startTime)
      .slice(0, maxEntries)
      // Integer ms: sub-millisecond precision is noise at gantt resolution, and
      // rounding trims ~10 JSON bytes per number across up-to-200 bars.
      .map((entry) => ({
        url: sanitizeUrl(entry.name, urlClass),
        start_time: Math.round(entry.startTime),
        end_time: Math.round(entry.responseEnd),
        initiator_type: entry.initiatorType,
        ...(entry.cache !== undefined ? { cache: entry.cache } : {}),
      }))
  );
}

// API fetch/XHR bars → LazyEvent metrics.api_request_timings.
export function collectApiRequestTimings(
  cutoffTime: number,
  options: CollectTimingsOptions = {},
): RequestTiming[] {
  return collectTimings(
    cutoffTime,
    ALLOWED_INITIATOR_TYPES,
    options.maxEntries ?? MAX_TIMINGS_PER_FIELD,
    "api",
  );
}

// Static script/link/css bars → LazyEvent metrics.resource_timings.
export function collectResourceTimings(
  cutoffTime: number,
  options: CollectTimingsOptions = {},
): RequestTiming[] {
  return collectTimings(
    cutoffTime,
    RESOURCE_BAR_INITIATOR_TYPES,
    options.maxEntries ?? MAX_TIMINGS_PER_FIELD,
    "asset",
  );
}

// Number of equal slices of [0, cutoff] in ImageSummary.concurrency — fixed,
// so the aggregate's wire size does not grow with image count.
const IMAGE_CONCURRENCY_BUCKETS = 10;

// Fold every pre-cutoff <img> load into one aggregate → LazyEvent
// metrics.image_summary. Concurrency comes from a start(+1)/end(−1) sweep:
// between consecutive events the number of in-flight downloads is constant,
// and each bucket keeps the max level of any interval touching it. undefined
// when no image completed before the cutoff (retry-on-next-flush semantics,
// same as the timing arrays).
export function collectImageSummary(
  cutoffTime: number,
): ImageSummary | undefined {
  const entries =
    typeof window !== "undefined"
      ? window.__xviewPerfTiming__?.resourceTimingEntries
      : undefined;
  if (entries === undefined) return undefined;

  const images = entries
    .filter((entry) => entry.initiatorType === "img")
    .filter((entry) => entry.startTime <= cutoffTime)
    .filter((entry) => entry.responseEnd > 0)
    .filter((entry) => entry.responseEnd >= entry.startTime);
  if (images.length === 0) return undefined;

  let earliestStart = Infinity;
  let latestEnd = -Infinity;
  let totalLoadMs = 0;
  const events: Array<{ time: number; delta: number }> = [];
  for (const image of images) {
    earliestStart = Math.min(earliestStart, image.startTime);
    latestEnd = Math.max(latestEnd, image.responseEnd);
    totalLoadMs += image.responseEnd - image.startTime;
    events.push(
      { time: image.startTime, delta: 1 },
      { time: image.responseEnd, delta: -1 },
    );
  }
  // Ends before starts at ties: back-to-back loads don't count as overlap.
  events.sort((a, b) => a.time - b.time || a.delta - b.delta);

  const concurrency = new Array<number>(IMAGE_CONCURRENCY_BUCKETS).fill(0);
  const bucketWidth = cutoffTime / IMAGE_CONCURRENCY_BUCKETS;
  let level = 0;
  let previousTime = 0;
  for (const event of events) {
    if (level > 0 && event.time > previousTime && bucketWidth > 0) {
      // [previousTime, event.time) ran at `level` concurrent downloads —
      // raise the max of every bucket it touches. Portions beyond the cutoff
      // fall outside the lane and simply aren't marked.
      const from = Math.max(0, Math.floor(previousTime / bucketWidth));
      const to = Math.min(
        IMAGE_CONCURRENCY_BUCKETS - 1,
        Math.ceil(event.time / bucketWidth) - 1,
      );
      for (let i = from; i <= to; i++) {
        concurrency[i] = Math.max(concurrency[i]!, level);
      }
    }
    level += event.delta;
    previousTime = event.time;
  }

  return {
    count: images.length,
    earliest_start: Math.round(earliestStart),
    latest_end: Math.round(latestEnd),
    total_load_ms: Math.round(totalLoadMs),
    concurrency,
  };
}
